import { tap, mergeMap, switchMap, delay, map, filter } from "rxjs/operators";
import { formOperation } from "../../wrappers/formOperation";
import { TActionsDefinitionsList } from "./TAction";
import { action, routeAction } from "./actionFunctions";
import { TJobsFormIO } from "../../../../domain/codecs/formIO/JobsIO";
import { TStateLens } from "../../../../domain/codecs/state";
import { TCodecSetState, TGetCodecState } from "../applyActions";
import { Observable } from "rxjs";
import { TStaffUser } from "../../../../domain/codecs/User";
import { TListingRoadblocksDashboardForm } from "../../../../domain/codecs/form/ListingRoadblockForm";
import { RoadblockAssignForm, RoadblockAwaitForm, RoadblockCancelAwaitForm } from "../../../../domain/codecs/form/RoadblockForm";
import { array, option } from "fp-ts";
import { pipe } from "fp-ts/lib/pipeable";
import { booleanFold } from "../../../../shared/src/utilsByDomain/option/booleanFold";
import { getCurrentBrandHostnameAndEnv, getCurrentBrandPrefix } from "../../../../domain/models/Enviroment";
import { TJob1 } from "../../../../domain/codecs/Job";
const EnvBrandPrefix = getCurrentBrandPrefix("web", window.location.hostname);
const IsSailLegal = EnvBrandPrefix === "sl";
const IsSailHomes = getCurrentBrandHostnameAndEnv("web", window.location.hostname) === "sailhomes";
const roadblockPageLimit = 50;

export const actions: TActionsDefinitionsList = [
    // ON VIEW
    action("VIEW_JOBS", (obs$, lens, setState, getState) => {
        obs$.pipe(
            tap(() => setState(lens.jobs.data.status.set("loading"))),
            mergeMap(
                () => formOperation("GetJobs", getState().jobs.data),
                (payload, data) => [payload, data] as [string | undefined, TJobsFormIO]
            ),
            tap(([payload, data]) =>
                setState(({ ...s }) => {
                    // [REFACTOR] Surley this can be neater and easier to reason about?
                    s.jobs.data = data;
                    s.jobs.case_filtered_by_user_id = "anyone";

                    if (
                        s.jobs.filter_has_been_changed_by_user === false 
                        && payload !== undefined
                        && isUserInStaffUserArray(data.output.filterable_case_users, payload)
                    ) {
                        s.jobs.case_filtered_by_user_id = payload;
                    }

                    return s;
                })
            ),
        ).subscribe();
    }),

    routeAction("VIEW_CRM_LISTING", (obs$, lens, set, get) => {
        obs$.pipe(
            $getJobs(lens, set, get),
        ).subscribe();
    }),
    routeAction("VIEW_CRM_ENQUIRY", (obs$, lens, set, get) => {
        obs$.pipe(
            $getJobs(lens, set, get),
        ).subscribe();
    }),
    routeAction("VIEW_CRM_SEARCH", (obs$, lens, set, get) => {
        obs$.pipe(
            filter(() => IsSailHomes),
            $getJobs(lens, set, get),
        ).subscribe();

        obs$.pipe(
            filter(() => IsSailHomes),
            tap(() => set(lens.overlay.listing_roadblocks.status.set("loading"))),
            $getListingRoadblocks(lens, set, get),
        ).subscribe();

        obs$.pipe(
            filter(() => IsSailHomes),
            $getListingRoadblocksWeekCounts(lens, set, get),
        ).subscribe();
    }),
    action("SH_LISTING_ROADBLOCK_ASSIGN", (obs$: Observable<{roadblock_id: string, assigned_to_user_id: string}>, lens, set, get) => {
        obs$.pipe(
            mergeMap((payload) => formOperation("AssignRoadblock", {
                ...RoadblockAssignForm.newDefault(),
                edited: payload,
                status: "requiresSubmission",
            })),
            $getListingRoadblocks(lens, set, get),
        ).subscribe();
    }),
    action("SH_LISTING_ROADBLOCK_AWAIT", (obs$: Observable<{roadblock_id: string, awaitable_entities_id: string, days_override: number | null}>, lens, set, get) => {
        obs$.pipe(
            mergeMap((payload) => formOperation("AwaitRoadblock", {
                ...RoadblockAwaitForm.newDefault(),
                edited: payload,
                status: "requiresSubmission",
            })),
            // Awaiting moves the roadblock to the day it comes back, so the strip's counts
            // are stale the moment the list refreshes without them.
            $getListingRoadblocks(lens, set, get),
            $getListingRoadblocksWeekCounts(lens, set, get),
        ).subscribe();
    }),
    action("SH_LISTING_ROADBLOCK_CANCEL_AWAIT", (obs$: Observable<{roadblock_id: string}>, lens, set, get) => {
        obs$.pipe(
            mergeMap((payload) => formOperation("CancelRoadblockAwait", {
                ...RoadblockCancelAwaitForm.newDefault(),
                edited: payload,
                status: "requiresSubmission",
            })),
            $getListingRoadblocks(lens, set, get),
            $getListingRoadblocksWeekCounts(lens, set, get),
        ).subscribe();
    }),
    action("SH_LISTING_ROADBLOCKS_FILTER_CHANGE", (obs$: Observable<TListingRoadblocksDashboardForm>, lens, set, get) => {
        obs$.pipe(
            // Paging is per bucket, so a date change that kept the old offset would open
            // the new day halfway down a list it has no rows for.
            tap((form) => {
                set(lens.overlay.listing_roadblocks.set({
                    ...form,
                    status: "loading",
                    edited: { ...form.edited, offset: 0 },
                }));
                // The strip's numbers carry the same type filter as the list, so leaving
                // them behind would show "12 overdue" above a list filtered down to 3.
                set(lens.overlay.listing_roadblocks_week_counts.edited.listing_roadblock_type.set(form.edited.listing_roadblock_type));
                set(lens.overlay.listing_roadblocks_week_counts.edited.checklist_item_type.set(form.edited.checklist_item_type));
            }),
            $getListingRoadblocks(lens, set, get),
        ).subscribe();

        obs$.pipe(
            $getListingRoadblocksWeekCounts(lens, set, get),
        ).subscribe();
    }),
    action("LEGAL_CASE_DETAILS_VIEW", (obs$, lens, set, get) => {
        obs$.pipe(
            $getJobs(lens, set, get),
        ).subscribe();
    }),

    // NEW ROADBLOCKS/JOBS MERGER
    action("VIEW_ROADBLOCKS", (obs$, lens, setState, getState) => {
        obs$.pipe(
            $resetRoadblocks(lens, setState, getState),

            filter(() => getCurrentBrandHostnameAndEnv("web", window.location.hostname) === "saillegal"),
            tap(() => setState(lens.overlay.roadblocks.status.set("loading"))),
            $getRoabdlocks(lens, setState, getState),
        ).subscribe()
    }),

    action("FILTER_ROADBLOCKS", (obs$, lens, setState, getState) => {
        obs$.pipe(
            tap((form) => setState(lens.overlay.roadblocks.set(form))),
            filter(() => IsSailLegal),
            $resetRoadblocks(lens, setState, getState),
            tap(() => setState(lens.overlay.roadblocks.status.set("submitting"))),
            $getRoabdlocks(lens, setState, getState),
        ).subscribe()
    }),

    // [NOTE]
    // The jobs filter handles filter by type, handler and assignee
    // But only the change of the assignee requires a request.
    // The type and handler are done in the components.
    //
    // P.S The sooner we kill jobs the better.
    action("FILTER_JOBS", (obs$, lens, setState, getState) => {
        obs$.pipe(            
            tap((filter) =>
                setState(lens.jobs.data.input.user_id.set(filter.filter_by_user || filter.default_user_filter))
            ),
            tap((filter) =>
                setState(lens.jobs.data.input.is_anyone.set(filter.filter_by_user === null))
            ),
            tap((filter) =>
                setState(lens.jobs.case_filtered_by_user_id.set(filter.filter_by_handler || 'anyone'))
            ),
            
            map((filter) => [lens.jobs.filter.get()(getState()), filter]),
            tap(([oldFilter, newFilter]) => setState(lens.jobs.filter.set(newFilter))),
            // [NOTE]
            // This here check, is here to make sure we don't pointlessly request
            // jobs when the filter.type is changed.
            filter(([oldFilter, newFilter]) => oldFilter.filter_by_user !== newFilter.filter_by_user),
            $getJobs(lens, setState, getState),
        ).subscribe()
    }),

    action("REASSIGN_ADHOC_JOB", (obs$: Observable<{ job: TJob1, assignedTo: string }>, lens, setState, getState) => {
        obs$.pipe(
            mergeMap(({ job, assignedTo }) =>
                formOperation(
                    job.type === "sh_listing_enquiry_ad_hoc"
                        ? "ReassignListingEnquiryAdHocTask"
                        : "ReassignListingAdHocTask",
                    {
                        status: "requiresSubmission",
                        input: { id: "id" in job ? job.id : "", assigned_to: assignedTo },
                        output: {},
                        validationErrors: [],
                    },
                )
            ),
            $getJobs(lens, setState, getState),
        ).subscribe()
    }),

    action("DEFER_ADHOC_JOB", (obs$: Observable<{ job: TJob1, deferredDate: string }>, lens, setState, getState) => {
        obs$.pipe(
            mergeMap(({ job, deferredDate }) =>
                formOperation(
                    job.type === "sh_listing_enquiry_ad_hoc"
                        ? "DeferListingEnquiryAdHocTask"
                        : "DeferListingAdHocTask",
                    {
                        status: "requiresSubmission",
                        input: { id: "id" in job ? job.id : "", deferred_date: deferredDate },
                        output: {},
                        validationErrors: [],
                    },
                )
            ),
            $getJobs(lens, setState, getState),
        ).subscribe()
    }),

    action("LOAD_MORE_ROADBLOCKS", (obs$, lens, setState, getState) => {
        obs$.pipe(
            $incrementRoadblockOffset(lens, setState, getState),

            filter(() => getCurrentBrandHostnameAndEnv("web", window.location.hostname) === "saillegal"),
            tap(() => setState(lens.overlay.roadblocks.status.set("submitting"))),
            $getRoabdlocks(lens, setState, getState),
        ).subscribe()
    }),
];

const $getJobs = <T>(lens: TStateLens, setState: TCodecSetState, getState: TGetCodecState) => 
    (obs$: Observable<T>) =>
        obs$.pipe(
            tap(() => setState(lens.jobs.data.status.set("submitting"))),
            mergeMap(() => formOperation("GetJobs", lens.jobs.data.get()(getState()))),
            tap((response) => setState(lens.jobs.data.set(response))),
        )
;

const $resetRoadblocks = <T>(lens: TStateLens, setState: TCodecSetState, getState: TGetCodecState) => 
    (obs$: Observable<T>) =>
        obs$.pipe(
            tap(() => setState(lens.overlay.roadblocks.edited.offset.set(0))),
            tap(() => setState(lens.overlay.roadblocks.edited.limit.set(roadblockPageLimit))),
            tap(() => setState(lens.overlay.roadblocks.children.roadblocks.set([]))),
        )
;

const $incrementRoadblockOffset = <T>(lens: TStateLens, setState: TCodecSetState, getState: TGetCodecState) => 
    (obs$: Observable<T>) =>
        obs$.pipe(
            map(() => lens.overlay.roadblocks.edited.offset.get()(getState())),
            tap((currentOffset) => setState(lens.overlay.roadblocks.edited.offset.set(currentOffset + roadblockPageLimit))),
        )
;

const $getListingRoadblocks = <T>(lens: TStateLens, setState: TCodecSetState, getState: TGetCodecState) =>
    (obs$: Observable<T>) =>
        obs$.pipe(
            switchMap(() => formOperation("GetListingRoadblocksDashboard", lens.overlay.listing_roadblocks.get()(getState()))),
            tap((response) => setState(lens.overlay.listing_roadblocks.set(response))),
        )
;

const $getListingRoadblocksWeekCounts = <T>(lens: TStateLens, setState: TCodecSetState, getState: TGetCodecState) =>
    (obs$: Observable<T>) =>
        obs$.pipe(
            tap(() => setState(lens.overlay.listing_roadblocks_week_counts.status.set("submitting"))),
            switchMap(() => formOperation("GetListingRoadblocksDashboardWeekCounts", lens.overlay.listing_roadblocks_week_counts.get()(getState()))),
            tap((response) => setState(lens.overlay.listing_roadblocks_week_counts.set(response))),
        )
;

const $getRoabdlocks = <T>(lens: TStateLens, setState: TCodecSetState, getState: TGetCodecState) => 
    (obs$: Observable<T>) =>
        obs$.pipe(
            switchMap(() => formOperation("GetRoadblocks", lens.overlay.roadblocks.get()(getState()))),
            map((response) => [response, lens.overlay.roadblocks.get()(getState())]),
            tap(([response, roadblocks]) => setState(lens.overlay.roadblocks.set({
                ...response,
                children: {
                    ...response.children,
                    roadblocks: roadblocks.children.roadblocks.concat(response.children.roadblocks)
                }
            }))),
            delay(300),
            tap(() => setState(lens.overlay.roadblocks.status.set("untouched"))),
        )
;

const isUserInStaffUserArray = (users: Array<TStaffUser>, id: string) =>
    pipe(
        users,
        array.findFirst((user) => user.id === id),
        booleanFold
    )
;
